import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Flex,
  Stack,
  Text,
} from '@chakra-ui/react';
import type {
  VoucherEligibilityItem,
  VoucherGetByCodeResponse,
} from '@diamond/shared/types';
import { useVoucherStore } from '@diamond/sol-buyer-context';
import Image from 'next/image';

import VoucherCard from './VoucherCard';

type VoucherStatusFilter = 'all' | 'available' | 'disabled';
type VoucherDisplayItem = VoucherEligibilityItem | VoucherGetByCodeResponse;

/**
 * Filter vouchers by eligibility status
 * Extracted as utility to avoid duplication
 */
export const filterAvailableVouchers = (
  vouchers: VoucherDisplayItem[]
): VoucherDisplayItem[] =>
  vouchers.filter((voucher) =>
    'is_eligible' in voucher ? voucher.is_eligible : true
  );

export const filterDisabledVouchers = (
  vouchers: VoucherDisplayItem[]
): VoucherDisplayItem[] =>
  vouchers.filter((voucher) =>
    'is_eligible' in voucher ? !voucher.is_eligible : false
  );

interface VoucherListSectionProps {
  vouchers: VoucherDisplayItem[];
  statusFilter: VoucherStatusFilter;
  searchTerm: string;
  onVoucherApplied: (voucher: VoucherDisplayItem) => void;
  cardActionMode?: 'apply' | 'add_to_cart';
  onVoucherShortcutAddToCart?: (voucher: VoucherDisplayItem) => void;
  hideDisabledSection?: boolean;
  onVoucherClick?: (voucher: VoucherDisplayItem) => void;
  isVoucherDisabledByCombination?: (voucher: VoucherDisplayItem) => boolean;
  onCartSyncStart?: () => void;
  onCartSyncComplete?: () => void;
}

const VoucherListSection = ({
  vouchers,
  statusFilter,
  searchTerm,
  onVoucherApplied,
  cardActionMode = 'apply',
  onVoucherShortcutAddToCart,
  hideDisabledSection = false,
  onVoucherClick,
  isVoucherDisabledByCombination,
  onCartSyncStart,
  onCartSyncComplete,
}: VoucherListSectionProps) => {
  const isApplyMode = cardActionMode === 'apply';
  const isVoucherApplied = useVoucherStore((state) => state.isVoucherApplied);
  const isPendingVoucher = useVoucherStore((state) => state.isPendingVoucher);
  const searchLower = searchTerm.trim().toLowerCase();

  const filteredVouchers = vouchers.filter((voucher) => {
    if (!searchLower) return true;
    const code = voucher.code || '';
    const title = voucher.title || '';
    return (
      code.toLowerCase().includes(searchLower) ||
      title.toLowerCase().includes(searchLower)
    );
  });

  const availableVouchers = isApplyMode
    ? filterAvailableVouchers(filteredVouchers)
    : filteredVouchers;
  const disabledVouchers = isApplyMode
    ? filterDisabledVouchers(filteredVouchers)
    : [];

  const showAvailable = statusFilter === 'all' || statusFilter === 'available';
  const showDisabled = statusFilter === 'all' || statusFilter === 'disabled';

  const handleApply = (voucher: VoucherDisplayItem) => {
    onVoucherApplied(voucher);
  };

  return (
    <Stack spacing="16px">
      {showAvailable && (
        <Stack spacing="12px">
          <Text fontSize="20px" color="#000000">
            Tersedia
          </Text>
          {availableVouchers.length === 0 ? (
            <Flex
              justify="center"
              flexDirection="column"
              align="center"
              minH="200px"
            >
              <Image
                src="/images/voucher/voucher_empty.png"
                alt="voucher empty"
                width={216}
                height={216}
              />
              <Text fontSize="12px" fontWeight="bold" textAlign="center" mt="4">
                Tidak ada voucher yang tersedia
              </Text>
            </Flex>
          ) : (
            <Stack spacing="12px">
              {availableVouchers.map((voucher) => {
                const isApplied = isApplyMode
                  ? isVoucherApplied(voucher.code)
                  : false;
                const isDisabledByCombination =
                  isApplyMode
                    ? isVoucherDisabledByCombination?.(voucher) ?? false
                    : false;
                const messageRuleProducts =
                  'message_rule_products' in voucher &&
                  voucher.message_rule_products
                    ? voucher.message_rule_products
                    : undefined;

                return (
                  <VoucherCard
                    key={voucher.id}
                    voucher={voucher}
                    onApply={isApplyMode ? handleApply : undefined}
                    onShortcutAddToCart={onVoucherShortcutAddToCart}
                    actionMode={cardActionMode}
                    isApplied={isApplied}
                    isProcessing={
                      isApplyMode ? isPendingVoucher(voucher.code) : false
                    }
                    isDisabled={isDisabledByCombination && !isApplied}
                    messageRuleProducts={messageRuleProducts}
                    onClick={onVoucherClick}
                    onCartSyncStart={onCartSyncStart}
                    onCartSyncComplete={onCartSyncComplete}
                  />
                );
              })}
            </Stack>
          )}
        </Stack>
      )}

      {showDisabled &&
        !hideDisabledSection &&
        (disabledVouchers.length > 0 || availableVouchers.length > 0) && (
          <Accordion
            allowMultiple
            defaultIndex={disabledVouchers.length ? [0] : []}
          >
            <AccordionItem border="none">
              <AccordionButton
                bg="transparent"
                _hover={{ bg: 'transparent' }}
                px={0}
              >
                <Text flex="1" color="#000000" textAlign="left" fontSize="20px">
                  Belum dapat digunakan
                </Text>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel px={0}>
                {disabledVouchers.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">
                    Voucher Tidak Ditemukan
                  </Text>
                ) : (
                  <Stack spacing="12px">
                    {disabledVouchers.map((voucher) => {
                      const messageRuleProducts =
                        'message_rule_products' in voucher &&
                        voucher.message_rule_products
                          ? voucher.message_rule_products
                          : undefined;

                      return (
                        <VoucherCard
                          key={voucher.id}
                          voucher={voucher}
                          isDisabled
                          messageRuleProducts={messageRuleProducts}
                          onClick={onVoucherClick}
                          onCartSyncStart={onCartSyncStart}
                          onCartSyncComplete={onCartSyncComplete}
                        />
                      );
                    })}
                  </Stack>
                )}
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        )}

      {!showAvailable &&
        !showDisabled &&
        availableVouchers.length === 0 &&
        disabledVouchers.length === 0 && (
          <Text fontSize="sm" color="gray.500">
            Tidak ada voucher.
          </Text>
        )}
    </Stack>
  );
};

export default VoucherListSection;
export type { VoucherStatusFilter };
